import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Status, SyncAssetsResponse } from "@/screens/creatives/types";
import { useEffect, useState, useRef } from "react";
import { SqButton } from "../squircle-ui/button";
import { cn } from "@/lib/utils";
import { RiResetRightFill } from "@remixicon/react";

type Props = {
  syncStatus: SyncAssetsResponse;
  onRetry: () => void;
  isRetrying?: boolean;
};

const formatEstimatedTime = (estimated: { min: number; max: number }): string => {
  // Use the max value for display, always in minutes
  const minutes = estimated.max;
  return `${minutes} min${minutes !== 1 ? "s" : ""} left`;
};

const formatDateRangeInMonths = (
  firstSyncedDate: string,
  lastSyncedDate: string,
): string => {
  const startDate = new Date(firstSyncedDate);
  const endDate = new Date(lastSyncedDate);

  // Calculate difference in months
  const months =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth());

  if (months <= 1) {
    return "Last 1 month";
  } else {
    return `Last ${months} months`;
  }
};

const SyncedAdsStatus = ({ syncStatus, onRetry, isRetrying }: Props) => {
  const { pending, total } = syncStatus.sync_details;
  const prevStatusRef = useRef<Status>(syncStatus.status);

  // State detection based on flowchart logic (NOT message text)
  const isFailed = syncStatus.status === "failed";
  const isPending = syncStatus.status === "pending";
  const isSuccess = syncStatus.status === "success";

  // Pending states:
  // - Syncing Ads (with count): status=pending, total > 0
  // - In Queue (with time): status=pending, total=0, estimated_completion available
  // - Syncing Ads (no count): status=pending, total=0, no estimated_completion
  const isSyncingWithCount = isPending && total > 0;
  const isInQueue =
    isPending && total === 0 && !!syncStatus.estimated_completion;
  const isSyncingNoCount =
    isPending && total === 0 && !syncStatus.estimated_completion;

  // Success states:
  // - Ads Synced: status=success, total > 0, pending = 0
  // - No ads available: status=success, total = 0
  const isAdsSynced = isSuccess && total > 0 && pending === 0;
  const isNoAdsAvailable = isSuccess &&  syncStatus.global_ad_count === 0;

  // Calculate progress percentage
  const progressPercent = total > 0 ? Math.floor(((total - pending) / total) * 100) : 0;

  // Determine progress bar color
  const getProgressBarColor = () => {
    if (isInQueue || isSyncingNoCount) return "bg-brand-primary-400";
    return "bg-brand-orange";
  };

  // Visibility states
  const [isVisible, setIsVisible] = useState<boolean>(isPending || isFailed);
  const [shouldRender, setShouldRender] = useState<boolean>(
    isPending || isFailed,
  );
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // When syncing with count and estimated time is available, alternate display every 5s
  const [showCountNotEstimate, setShowCountNotEstimate] = useState(true);
  const hasEstimateWhileSyncing =
    isSyncingWithCount && !!syncStatus.estimated_completion;
  // Sequential fade: displayState is what's in the slot; phase drives fade-out then fade-in
  const [displayState, setDisplayState] = useState<"count" | "estimate">("count");
  const [phase, setPhase] = useState<"visible" | "fading-out" | "fading-in">(
    "visible",
  );

  useEffect(() => {
    if (!hasEstimateWhileSyncing) {
      setShowCountNotEstimate(true);
      setDisplayState("count");
      setPhase("visible");
      return;
    }
    const interval = setInterval(() => {
      setShowCountNotEstimate((prev) => !prev);
    }, 5000);
    return () => clearInterval(interval);
  }, [hasEstimateWhileSyncing]);

  // When target changes, start fade-out
  useEffect(() => {
    if (!hasEstimateWhileSyncing) return;
    const target = showCountNotEstimate ? "count" : "estimate";
    if (displayState !== target && phase === "visible") {
      setPhase("fading-out");
    }
  }, [hasEstimateWhileSyncing, showCountNotEstimate, displayState, phase]);

  // After fade-out ends: swap content and start fade-in
  const onFadeTransitionEnd = () => {
    if (phase === "fading-out") {
      const target = showCountNotEstimate ? "count" : "estimate";
      setDisplayState(target);
      setPhase("fading-in");
    }
  };

  // When in fading-in, trigger opacity 0→1 after a paint so transition runs
  useEffect(() => {
    if (phase !== "fading-in") return;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase("visible"));
    });
    return () => cancelAnimationFrame(id);
  }, [phase]);

  useEffect(() => {
    // Clear any existing timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    if (isPending || isFailed || isNoAdsAvailable) {
      // Show and stay visible
      setIsVisible(true);
      setShouldRender(true);
    } else if (isAdsSynced && prevStatusRef.current === "pending") {
      // Show immediately, then hide after 5 seconds
      setIsVisible(true);
      setShouldRender(true);
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    } else {
      // Hide for any other state
      setIsVisible(false);
    }

    prevStatusRef.current = syncStatus.status;

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [syncStatus.status, isPending, isFailed, isAdsSynced, isNoAdsAvailable]);

  const onTransitionEnd = () => {
    if (!isVisible) {
      setShouldRender(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`mb-2 px-[14px] py-[7px] flex flex-col gap-[2px] rounded-[8px] border-none squircle squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}
            onTransitionEnd={onTransitionEnd}
          >
            {/* Progress bar - show for pending states and success with ads */}
            {(isPending || isAdsSynced) && (
              <div className="w-full py-[6px]">
                <div className="w-full h-[3px] bg-border-gray rounded-full overflow-hidden relative">
                  {isInQueue || isSyncingNoCount ? (
                    // Indeterminate loader when in queue or syncing without count
                    <div
                      className="h-full w-[50px] bg-brand-primary-400 absolute rounded-full"
                      style={{ animation: "slideLoader 1.5s ease-in-out infinite" }}
                    />
                  ) : (
                    // Determinate progress bar (syncing with count or completed)
                    <div
                      className={`h-full rounded-full ${getProgressBarColor()} transition-all duration-500 ease-out`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Content */}
            {isFailed ? (
              // Failed state: status=failed
              <div className="flex justify-between items-center">
                <div className="flex flex-col">
                  <span className="text-[11px] font-normal tracking-[0.24px] text-brand-primary-400 leading-[20px]">
                    {syncStatus.message || "API limit exceeded"}
                  </span>
                  <span className="text-[12px] font-medium tracking-[0.24px] text-[#FF3333] leading-[20px]">
                    Sync Failed
                  </span>
                </div>

                <SqButton
                  variant="outline"
                  size="md"
                  onClick={onRetry}
                  disabled={isRetrying}
                  className={cn(
                    "w-[32px] h-[32px]",
                    isRetrying ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
                  )}
                >
                  <RiResetRightFill
                    size={14}
                    className={`flex-shrink-0 text-brand-primary-400 ${
                      isRetrying ? "animate-spin" : ""
                    }`}
                  />
                </SqButton>
              </div>
            ) : isNoAdsAvailable ? (
              // No ads available: status=success, total=0
              <div className="flex flex-col">
                <span className="text-[12px] font-normal tracking-[0.24px] text-brand-primary-400 leading-[20px]">
                  {syncStatus.first_synced_date && syncStatus.last_synced_date
                    ? formatDateRangeInMonths(
                        syncStatus.first_synced_date,
                        syncStatus.last_synced_date,
                      )
                    : "No date range"}
                </span>
                <span className="text-[12px] font-medium tracking-[0.24px] leading-[20px] text-brand-primary-600">
                  {syncStatus.message || "No ads available"}
                </span>
              </div>
            ) : isAdsSynced ? (
              // Ads synced: status=success, total>0, pending=0
              <div className="flex justify-between items-center min-h-[20px]">
                <span className="text-[12px] font-medium tracking-[0.24px] leading-[20px] text-brand-primary-800">
                  {syncStatus.message || "Ads Synced"}
                </span>
                <span className="text-[12px] font-normal tracking-[0.24px] text-brand-primary-600 leading-[20px]">
                  {total} / {total}
                </span>
              </div>
            ) : isInQueue ? (
              // In Queue: status=pending, total=0, estimated_completion available
              <div className="flex justify-between items-center min-h-[20px]">
                <span className="text-[12px] font-medium tracking-[0.24px] leading-[20px] text-brand-primary-800">
                  {syncStatus.message || "In Queue"}
                </span>
                <span className="text-[12px] font-normal tracking-[0.24px] text-brand-primary-600 leading-[20px]">
                  {formatEstimatedTime(syncStatus.estimated_completion!)}
                </span>
              </div>
            ) : isSyncingWithCount ? (
              // Syncing with count: status=pending, total>0 (alternate count vs estimated time if available)
              <div className="flex justify-between items-center overflow-hidden min-h-[20px]">
                <span className="min-w-max text-[12px] font-medium tracking-[0.24px] leading-[20px] text-brand-primary-800">
                  {syncStatus.message || "Syncing Ads"}
                </span>
                {hasEstimateWhileSyncing ? (
                  <span className="flex-1 min-w-0 flex justify-end relative min-h-[20px]">
                    <span
                      className={cn(
                        "absolute inset-0 flex items-center justify-end text-[12px] font-normal tracking-[0.24px] text-brand-primary-600 leading-[20px] transition-opacity duration-300",
                        phase === "visible" ? "opacity-100" : "opacity-0",
                      )}
                      onTransitionEnd={onFadeTransitionEnd}
                      aria-hidden={phase !== "visible"}
                    >
                      {displayState === "count"
                        ? `${total - pending} / ${total}`
                        : formatEstimatedTime(
                            syncStatus.estimated_completion!,
                          )}
                    </span>
                    {/* Invisible spacer to hold height/width - block so it establishes height reliably */}
                    <span className="invisible block text-[12px] font-normal tracking-[0.24px] leading-[20px] h-[20px]">
                      {formatEstimatedTime(syncStatus.estimated_completion!)}
                    </span>
                  </span>
                ) : (
                  <span className="text-[12px] font-normal tracking-[0.24px] text-brand-primary-600 leading-[20px]">
                    {total - pending} / {total}
                  </span>
                )}
              </div>
            ) : (
              // Syncing without count: status=pending, total=0, no estimated_completion
              <div className="flex justify-between items-center min-h-[20px]">
                <span className="text-[12px] font-medium tracking-[0.24px] leading-[20px] text-brand-primary-800">
                  {syncStatus.message || "Syncing Ads"}
                </span>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent hidden={!isInQueue} align="end">
          <p>High traffic. Account sync queued.</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SyncedAdsStatus;
